/* eslint-disable no-useless-escape */
/* eslint-disable radix */

import { isEmptyObject } from './generic';

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function isEmailValid(email) {
  if (!email) {
    return false;
  }

  return emailRegex.test(email);
}

export function cpfValidator(cpf) {
  if (!cpf) cpf = '';
  cpf = cpf.replace(/[^\d]+/g, '');
  let Soma;
  let Resto;
  Soma = 0;
  if (
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  )
    return false;
  for (let i = 1, l = 9; i <= l; i++) {
    Soma = Soma + Number.parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  Resto = (Soma * 10) % 11;
  if (Resto === 10 || Resto === 11) Resto = 0;
  if (Resto !== Number.parseInt(cpf.substring(9, 10))) {
    return false;
  }
  Soma = 0;
  for (let j = 1, k = 10; j <= k; j++) {
    Soma = Soma + Number.parseInt(cpf.substring(j - 1, j)) * (12 - j);
  }

  Resto = (Soma * 10) % 11;
  if (Resto === 10 || Resto === 11) Resto = 0;

  if (Resto !== Number.parseInt(cpf.substring(10, 11))) return false;

  return true;
}

export function isPostalCodeValid(cep) {
  const cepRegex = /^\d{5}(-?)\d{3}$/g;
  const cepNoMask = removeMask(cep);

  return cepNoMask.length === 8 && cepRegex.test(cepNoMask);
}

export function removeMask(value: string): string {
  return value?.length > 0 ? value.replace(/[^a-z0-9]/gi, '') : '';
}

/**
 * Mobile numbers from `react-phone-input-2` with `country="br"`: digits-only string
 * (e.g. `5511987654321`). Default `55` / partial values must be rejected (same idea as register-form).
 * Also accepts 11-digit national values (`11987654321`) when synced from profile.
 */
export function isBrazilianMobilePhoneInputValid(
  value: string | undefined | null,
): boolean {
  if (value == null || String(value).trim() === '') return false;
  const digits = removeMask(String(value));
  if (digits === '55' || digits.length < 11) return false;
  if (digits.startsWith('55')) {
    if (digits.length < 13) return false;
    return /^55\d{2}9\d{8}$/.test(digits);
  }
  if (digits.length === 11) {
    return /^\d{2}9\d{8}$/.test(digits);
  }
  return false;
}

/**
 * Checkout / order: accept legacy or malformed stored numbers (short national fragments, etc.)
 * so users are not blocked, while still rejecting empty values, country-only `55`, and obvious
 * partial entry in the +55 widget (3–6 digits after stripping when starting with `55`).
 */
export function isCheckoutContactPhoneAcceptableForOrder(
  value: string | undefined | null,
): boolean {
  if (value == null || String(value).trim() === '') return false;
  const digits = removeMask(String(value));
  if (digits.length === 0 || digits === '55') return false;

  if (isBrazilianMobilePhoneInputValid(value)) return true;

  if (digits.startsWith('55') && digits.length >= 3 && digits.length <= 6) {
    return false;
  }

  return digits.length >= 4 && digits.length <= 15;
}

export function hasNumber(string) {
  return /\d/.test(string);
}

export function hasUpperCase(string) {
  return string !== string.toLowerCase();
}

export function hasLowerCase(string) {
  return string !== string.toUpperCase();
}

export function validateCartItems(
  inventorySkus = {},
  { commerceItems = {} } = {},
) {
  const outOfStock = Object.values(commerceItems).filter(
    ({ catRefId }) =>
      inventorySkus[catRefId]?.default?.stockStatus !== 'IN_STOCK',
  );

  // const outOfStock = Object.values(inventorySkus)?.filter(
  //   inventorySku => inventorySku?.default?.stockStatus !== 'IN_STOCK'
  // );

  return !!outOfStock.length;
}

export function validateCartItemsProps({ commerceItems = [] } = {}) {
  return commerceItems.filter(({ dynamicProperties }) =>
    dynamicProperties.find(
      (dynProps) => dynProps.id === 'x_ean' && !dynProps.value,
    ),
  );
}

export function validateShippingMethod({ shippingGroups } = {}) {
  if (isEmptyObject(shippingGroups)) return true;

  return !Object.values(shippingGroups).every(
    ({ shippingMethod }) =>
      shippingMethod &&
      shippingMethod.value &&
      shippingMethod.value !== 'hardgoodShippingGroup',
  );
}

export function validateOrderAmount(
  { priceInfo: { total } } = {},
  payments = [],
) {
  const paymentsSum = payments.reduce(
    (acc, payment) => acc + payment.installmentsValue,
    0,
  );
  if (total !== paymentsSum) return true;

  return false;
}

const getRawRut = (rut) => {
  return String(rut).replace(/[^0-9a-z]/gi, '');
};

const calcRutDigit = (rut) => {
  let sum = 0;
  const reverseRut = getRawRut(rut).split('').reverse();

  for (let i = 0, j = 2; i < reverseRut.length; i++, j < 7 ? j++ : (j = 2)) {
    sum += reverseRut[i] * j;
  }

  const result = 11 - (sum % 11);

  if (result === 11) return '0';
  if (result === 10) return 'k';

  return String(result);
};

export const isRutValid = (rut) => {
  if (typeof rut === 'string' || typeof rut === 'number') {
    const rawRut = getRawRut(rut);

    const rutDigit = rawRut.slice(-1);
    const rutWithoutDigit = rawRut.slice(0, -1);

    const digitCalculated = calcRutDigit(rutWithoutDigit).toLowerCase();

    return digitCalculated === rutDigit.toLowerCase();
  }

  return false;
};

function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

// export function formatDate(date) {
//   if (!date) return;

//   return [
//     padTo2Digits(date.getDate()),
//     padTo2Digits(date.getMonth() + 1),
//     date.getFullYear(),
//   ].join('/');
// }

export const dateValidator = (dateString) => {
  if (
    !/^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/.test(
      dateString,
    ) &&
    !/^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{2}$/.test(
      dateString,
    )
  ) {
    return false;
  }
  if (dateString.length < 10) {
    return false;
  }
  //Test which seperator is used '/' or '-'
  const opera1 = dateString.split('/');
  const opera2 = dateString.split('-');
  const lopera1 = opera1.length;
  const lopera2 = opera2.length;
  let pdate;
  // Extract the string into month, date and year
  if (lopera1 > 1) {
    pdate = dateString.split('/');
  } else if (lopera2 > 1) {
    pdate = dateString.split('-');
  }
  const dd = Number.parseInt(pdate[0]);
  const mm = Number.parseInt(pdate[1]);
  const yy = Number.parseInt(pdate[2]);
  if (yy < 1900) {
    return false;
  }
  // Create list of days of a month [assume there is no leap year by default]
  const ListofDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (mm === 1 || mm > 2) {
    if (dd > ListofDays[mm - 1]) {
      return false;
    }
  }
  if (mm === 2) {
    let lyear = false;
    if ((!(yy % 4) && yy % 100) || !(yy % 400)) {
      lyear = true;
    }
    if (lyear === false && dd >= 29) {
      return false;
    }
    if (lyear === true && dd > 29) {
      return false;
    }
  }

  return true;
};

export const rgValidator = (value) => {
  const validateRg = /[@!#$%^&*()='+_"?°~`<>{}\\]/;
  const testJustLetters = /^[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ ]+$/;

  if (
    validateRg.test(value) === true ||
    testJustLetters.test(value) ||
    value.length < 2
  ) {
    return false;
  }

  return true;
};

/**
 * Returns validation on environment information.
 *
 * @return {Boolean} is production environment
 */
export function isProduction() {
  if (!window || !window.location) {
    return false;
  }

  return window.location.hostname == 'www.qualidoc.com.br';
}

export function dateOfBirthValidator(dateOfBirth: string) {
  return /(^0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4}$)/.test(
    dateOfBirth,
  );
}
